// Inspiration for this code: https://daankauwenberg.com/react-cookie-consent/

import React, {
  createContext,
  useReducer,
  useEffect,
  useState,
  useContext,
  ReactNode,
  Dispatch,
} from "react";
import CookiePopup from "./CookiePopup";

const COOKIE_NAME = "cookieconsent";

type Props = {
  children: ReactNode;
};

export type CookieConsentAction = {
  type: string;
};

type State = {
  isSet: boolean;
  isAccepted: boolean;
};

const initialState = {
  isSet: false,
  isAccepted: false,
};

const CookieConsentStateContext = createContext<State>(initialState);
const CookieConsentDispatchContext = createContext<
  Dispatch<CookieConsentAction>
>(() => {});

function getCookie() {
  const regex = new RegExp(
    `(?:(?:^|.*;\\s*)${COOKIE_NAME}\\s*\\=\\s*([^;]*).*$)|^.*$`
  );
  const cookie = document.cookie.replace(regex, "$1");
  return cookie.length ? JSON.parse(cookie) : undefined;
}

// Initial value is cookie value OR prefered value but not yet set
let initialCookieValue = getCookie() || initialState;

const CookieConsentProvider = ({ children }: Props) => {
  const [popupIsOpen, setPopupIsOpen] = useState<boolean>(
    !initialCookieValue.isSet
  );

  const [state, dispatch] = useReducer(
    (state: State, action: CookieConsentAction) => {
      switch (action.type) {
        case "accept":
          setPopupIsOpen(false);
          return {
            ...state,
            isSet: true,
            isAccepted: true,
          };
        case "decline":
          setPopupIsOpen(false);
          return {
            ...state,
            isSet: true,
            isAccepted: false,
          };
        case "showCookiePopup":
          setPopupIsOpen(true);
          return state;
        default:
          throw new Error();
      }
    },
    initialCookieValue
  );

  // Update the cookie when state changes
  useEffect(() => {
    document.cookie = `${COOKIE_NAME}=${JSON.stringify(state)}`;
  }, [state]);

  return (
    <CookieConsentStateContext.Provider value={state}>
      <CookieConsentDispatchContext.Provider value={dispatch}>
        {popupIsOpen && <CookiePopup dispatch={dispatch} />}
        {children}
      </CookieConsentDispatchContext.Provider>
    </CookieConsentStateContext.Provider>
  );
};

function useCookieConsentState() {
  const context = useContext(CookieConsentStateContext);
  if (context === undefined) {
    throw new Error("useCookieConsentState must be used within CookieProvider");
  }
  return context;
}

function useCookieConsentDispatch() {
  const context = useContext(CookieConsentDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useCookieConsentDispatch must be used within CookieProvider"
    );
  }
  return context;
}

export {
  CookieConsentProvider,
  useCookieConsentState,
  useCookieConsentDispatch,
};
